import React from 'react';
import { PrismicRichText, JSXMapSerializer } from '@prismicio/react';
import { RichTextField } from '@prismicio/types';
import Image from 'next/image';
import { getChartByName } from '@/components/charts/load-chart';
import PullQuote from '@/components/PullQuote';

// Rich-text shortcode: a paragraph that is exactly `[chart 'name']` renders the
// matching component from components/charts/. Quotes are optional (straight or curly).
const CHART_RE = /^\[chart\s+['"‘’“”]?([a-z0-9-]+)['"‘’“”]?\]$/i;

// Rich-text shortcode: a paragraph that starts with `> ` renders as a pull quote
// (components/PullQuote.tsx). An optional attribution follows the LAST ` — `
// (em/en dash, or ` -- `) and splits on ` · ` / ` | ` into source · context:
//   > The retention money can be identified clearly anytime. — Court of Appeal · Hsin Chong
// Wrapping quote marks are optional — the component adds typographic ones.
const QUOTE_PREFIX_RE = /^>\s*/;
const ATTRIBUTION_RE = /\s+(?:—|–|--)\s+([^—–]+)$/;
function parsePullQuote(text: string): { quote: string; source?: string; context?: string } | null {
  if (!QUOTE_PREFIX_RE.test(text)) return null;
  let quote = text.replace(QUOTE_PREFIX_RE, '').trim();
  let source: string | undefined;
  let context: string | undefined;
  const attr = quote.match(ATTRIBUTION_RE);
  if (attr && attr.index !== undefined) {
    quote = quote.slice(0, attr.index).trim();
    const parts = attr[1].split(/\s+[·|]\s+/).map((part) => part.trim()).filter(Boolean);
    [source, context] = parts;
  }
  quote = quote.replace(/^["“„]\s*/, '').replace(/\s*["”]$/, '');
  return quote ? { quote, source, context } : null;
}

// Prismic occasionally stores overlapping hyperlink spans in a block — e.g. a
// whole-sentence link with word-level links nested inside it (an auto-linker
// over-extending a span). PrismicRichText renders those as nested <a> tags,
// which is invalid HTML and throws a React hydration error. Flatten them: keep
// the most specific (shortest) link, drop any hyperlink that overlaps one already
// kept. Non-link spans (strong/em) are untouched — they nest inside <a> just fine.
function stripNestedHyperlinks(field: RichTextField): RichTextField {
  return field.map((block) => {
    if (!('spans' in block) || !block.spans || block.spans.length < 2) return block;
    const links = block.spans
      .filter((s) => s.type === 'hyperlink')
      .sort((a, b) => a.end - a.start - (b.end - b.start)); // shortest first
    if (links.length < 2) return block;
    const kept: typeof links = [];
    for (const s of links) {
      if (!kept.some((k) => s.start < k.end && k.start < s.end)) kept.push(s);
    }
    if (kept.length === links.length) return block; // no overlap → leave as-is
    const others = block.spans.filter((s) => s.type !== 'hyperlink');
    return { ...block, spans: [...others, ...kept] };
  }) as RichTextField;
}

interface CustomRichTextProps {
  field: RichTextField;
  components?: JSXMapSerializer;
}

const CustomRichText: React.FC<CustomRichTextProps> = ({ field, components }) => {
  const defaultComponents: JSXMapSerializer = {
    // `[chart 'name']` on its own line → render components/charts/<name>.tsx
    paragraph: ({ node, children }) => {
      const match = node.text.trim().match(CHART_RE);
      if (match) {
        const Chart = getChartByName(match[1]);
        return Chart ? (
          <div className="my-12">
            <Chart />
          </div>
        ) : null;
      }
      const pullQuote = parsePullQuote(node.text.trim());
      if (pullQuote) {
        return (
          <PullQuote source={pullQuote.source} context={pullQuote.context}>
            {pullQuote.quote}
          </PullQuote>
        );
      }
      return <p>{children}</p>;
    },
    preformatted: ({ node }) => <div dangerouslySetInnerHTML={{ __html: node.text }} />,
    image: ({ node }) => {
      if (!node.url) return null;

      const altText = node.alt?.trim() || '';
      const shouldShowCaption = altText.endsWith('#');
      const captionText = shouldShowCaption ? altText.slice(0, -1) : '';

      return (
        <figure className="my-6">
          <div className="relative w-full overflow-hidden rounded-md">
            <Image
              src={node.url}
              alt={node.alt || ''}
              width={node.dimensions?.width || 1200}
              height={node.dimensions?.height || 800}
              className="h-auto w-full"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 1200px"
            />
          </div>
          {shouldShowCaption && (
            <figcaption className="-mt-4 mb-10 text-center text-sm italic text-black/40">
              {captionText}
            </figcaption>
          )}
        </figure>
      );
    },
  };
  // Merge custom components with default
  const mergedComponents = { ...defaultComponents, ...components };

  return <PrismicRichText field={stripNestedHyperlinks(field)} components={mergedComponents} />;
};

export default CustomRichText;
